import { useSettings } from "contexts/SettingsManager/SettingsManager";

import type { Process } from "types/types";
import { PnlCategoriesEnum } from "types/enums";
import { useLatestSegmentForProcess } from "../expandedTileUtils";
import { DoubleLineModule, SingleLineModule } from "./ExpandedTileModules";
import { useSelector } from "store/useSelector";

/** Expanded Tile module showing skuName and/or skuId */
// tslint:disable-next-line: cognitive-complexity
export function TileSkuModule({
  showSkuName,
  showSkuId,
  process,
}: {
  showSkuName: boolean;
  showSkuId: boolean;
  process: Process;
}) {
  const latestSegment = useLatestSegmentForProcess(process.id);
  const pnlCategory = latestSegment?.pnl.category;
  const isUncollected = pnlCategory === PnlCategoriesEnum.uncollected;
  const { isLargeTiles, tileScaleMultiplier } = useSettings();
  const dataDump = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess[process.id]
  );
  const isNoSkuSelected = dataDump?.lastSegment?.skuCode === "no_sku_selected"; // TODO ?
  const skuId = isUncollected
    ? "--"
    : isNoSkuSelected
      ? ""
      : (dataDump?.lastSegment?.skuCode ?? null);
  const skuName = isUncollected
    ? "--"
    : isNoSkuSelected
      ? "-no sku selected-"
      : (dataDump?.lastSegment?.skuName ?? null);

  const isLoading =
    (showSkuName || showSkuId) &&
    !dataDump &&
    !isUncollected &&
    !isNoSkuSelected;

  // shrink font sizes if they would overflow
  const smallerFontSizeCSS = `font-size: ${12 * (isLargeTiles ? 1.5 : 1) * tileScaleMultiplier ** 0.45}px;`;
  return showSkuName && showSkuId && !isNoSkuSelected ? (
    <DoubleLineModule
      loading={isLoading}
      dataLine1={skuId}
      dataLine2={skuName}
      css={`
        .line-1 {
          ${isSkuStringTooLong({ skuString: skuId }) ? smallerFontSizeCSS : ""}
        }
        .line-2 {
          ${isSkuStringTooLong({ skuString: skuName, topRow: false })
            ? smallerFontSizeCSS
            : ""}
        }
      `}
    />
  ) : (
    <SingleLineModule
      loading={isLoading}
      data={showSkuName ? skuName : skuId}
      css={`
        ${(showSkuName && isSkuStringTooLong({ skuString: skuName })) ||
        (showSkuId && isSkuStringTooLong({ skuString: skuId }))
          ? smallerFontSizeCSS
          : ""}
        /* only bold the skuId, for consistency with double-line module above */
          ${showSkuName ? `font-weight: normal;` : ""}
      `}
    />
  );
}

const MAX_CHARS_TOP_ROW_BEFORE_SHRINK_FONT = 10;
const MAX_CHARS_BOTTOM_ROW_BEFORE_SHRINK_FONT = 18;

function isSkuStringTooLong({
  skuString,
  topRow = true,
}: {
  skuString: string | null;
  topRow?: boolean;
}) {
  const maxCharsBeforeShrinkFont = topRow
    ? MAX_CHARS_TOP_ROW_BEFORE_SHRINK_FONT
    : MAX_CHARS_BOTTOM_ROW_BEFORE_SHRINK_FONT;
  return skuString && skuString.length > maxCharsBeforeShrinkFont;
}
